import React, { createContext, lazy, memo, Suspense, useCallback, useContext, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { parseSpecification, specUrl } from './specLoader';
// Swagger UI's styles load with the portal's, before Portal.css so its overrides keep winning; its script loads on its own
// (SwaggerUI below), so the catalog doesn't wait for it.
import 'swagger-ui-react/swagger-ui.css';
import './Portal.css';
import { ResponseSlot, responseWrappers } from './ResponseExplorer';

const Navigation = createContext({});
const METHODS = ['get', 'post', 'put', 'patch', 'delete', 'options', 'head', 'trace'];
const OTHER = 'Other';
const SUMMARY_LIMIT = 50;
export const readableTag = tag => tag.replace(/[-_]/g, ' ').replace(/^./, c => c.toUpperCase());
// Catalog cards show at most 50 characters of a summary, leaving room for the repository; the tooltip keeps the full text.
export const shortSummary = summary => summary.length > SUMMARY_LIMIT ? `${summary.slice(0, SUMMARY_LIMIT - 1).trimEnd()}…` : summary;
// AppSync's Lambda-backed queries and mutations (the spec's `x-appsync-functions`, bundled from appsync/functions.yaml)
// are listed with the endpoints, in a repository of their own with a group per GraphQL type.
const APPSYNC_GROUP = { name: 'OT-APPSYNC-API', tags: ['Queries', 'Mutations'] };
const APPSYNC_TAGS = { Query: 'Queries', Mutation: 'Mutations' };
// Repositories come from the spec's `x-tagGroups` (Redoc's convention; Swagger UI ignores it), plus OT-APPSYNC-API when the
// spec lists AppSync functions. Tags they leave out go under "Other"; without any there is one unnamed group, a flat tag list.
export function repositoryGroups(spec, tags) {
  const tagGroups = [...(spec['x-tagGroups'] || []), ...(spec['x-appsync-functions']?.length ? [APPSYNC_GROUP] : [])];
  const groups = tagGroups.filter(group => group?.name).map(group => ({
    name: group.name, tags: (group.tags || []).filter(tag => tags.includes(tag)),
  }));
  const rest = tags.filter(tag => !groups.some(group => group.tags.includes(tag)));
  return [...groups, { name: groups.length ? OTHER : '', tags: rest }].filter(group => group.tags.length);
}
// An operation needs credentials when its `security` (else the spec's) lists a scheme; [] or a {} entry means none.
const requiresAuth = requirements => requirements.length > 0 && requirements.every(requirement => Object.keys(requirement || {}).length > 0);
// An API key is an x-api-key header parameter, or a security scheme for a header other than Authorization;
// anything else secured uses the Authorization token. An API with an API key is always authorized.
// authTypes lists how it authenticates ('token', 'apiKey'): one way for an endpoint, none when it is non-authorized.
function authOf(spec, item, operation) {
  const requirements = operation.security ?? spec.security ?? [];
  const schemes = requirements.flatMap(requirement => Object.keys(requirement || {})).map(name => spec.components?.securitySchemes?.[name]);
  const apiKey = [...(item.parameters || []), ...(operation.parameters || [])].some(p => p?.in === 'header' && /^x-api-key$/i.test(p.name || ''))
    || schemes.some(scheme => scheme?.type === 'apiKey' && !/^authorization$/i.test(scheme.name || ''));
  const secured = apiKey || requiresAuth(requirements);
  return { secured, authTypes: !secured ? [] : apiKey ? ['apiKey'] : ['token'] };
}
// An AppSync authorization mode's auth type: a JWT in the Authorization header is a token; IAM (signed requests) is neither.
const APPSYNC_AUTH_TYPES = { 'Cognito user pools': 'token', OIDC: 'token', 'Lambda authorizer': 'token', 'API key': 'apiKey' };
export function listOperations(spec) {
  const operations = Object.entries(spec.paths || {}).flatMap(([path, item]) => METHODS.filter(method => item[method]).map(method => {
    const details = implementationOf(spec, path, method);
    return {
      id: `${method}:${path}`, path, method, summary: item[method].summary || '',
      tags: item[method].tags?.length ? item[method].tags : ['Other'],
      deprecated: !!item[method].deprecated,
      ...authOf(spec, item, item[method]),
      // from x-ostrum: the other APIs and Lambdas its handler calls, and the AWS services its role allows;
      // null when that isn't known
      nestedCalls: Array.isArray(details?.nestedCalls) ? details.nestedCalls : null,
      awsServices: Array.isArray(details?.awsServices) ? details.awsServices : null,
      calledBy: Array.isArray(details?.calledBy) ? details.calledBy : null,
    };
  })).concat(appSyncFunctions(spec));
  const repositories = repositoryGroups(spec, [...new Set(operations.flatMap(op => op.tags))]).filter(group => group.name);
  return operations.map(op => ({ ...op, repos: repositories.filter(group => group.tags.some(tag => op.tags.includes(tag))).map(group => group.name) }));
}
// An AppSync function as a catalog entry: its GraphQL field in place of a path and its type (query, mutation) in place of a
// method; `appsync` holds its details. Swagger UI has nothing for it. It is authorized when its authModes are recorded, with
// every auth type they allow (a token and an API key, as a rule); without them it has no padlock and is neither Authorized
// nor Non-authorized. Its nested calls are its handler's, like an endpoint's (null when they weren't read).
function appSyncFunctions(spec) {
  return (spec['x-appsync-functions'] || []).filter(fn => fn?.function).map(fn => {
    const fields = (fn.operations || []).map(operation => operation.split('.'));
    const type = fields[0]?.[0] || '';
    const modes = Array.isArray(fn.authModes) && fn.authModes.length ? fn.authModes : null;
    return {
      id: `appsync:${fn.function}`, path: fields.map(([, field]) => field).join(', ') || fn.function,
      method: type.toLowerCase() || 'function', summary: fn.description || '', tags: [APPSYNC_TAGS[type] || OTHER],
      deprecated: false, secured: modes ? true : null,
      authTypes: [...new Set((modes || []).map(mode => APPSYNC_AUTH_TYPES[mode]).filter(Boolean))],
      nestedCalls: Array.isArray(fn.nestedCalls) ? fn.nestedCalls : null,
      awsServices: Array.isArray(fn.awsServices) ? fn.awsServices : null,
      calledBy: Array.isArray(fn.calledBy) ? fn.calledBy : null,
      appsync: fn,
    };
  });
}
// Search looks at endpoint paths only: every word must appear in the path.
// access: '' for all, 'secured' for authorized APIs, 'public' for non-authorized ones.
// authType: '' for any, 'token' or 'apiKey' for authorized APIs that can authenticate that way.
// nested: '' for any, 'with' for APIs that make nested calls, 'without' for those known to make none.
// services: AWS services the API's role must all allow ([] for any).
// callers: apps (OW, PW, OA, PD) the API must all be called from ([] for any).
export function searchOperations(operations, query, tag, access = '', authType = '', nested = '', services = [], callers = []) {
  const words = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
  return operations.filter(op => (!tag || op.tags.includes(tag)) && (!access || op.secured === (access === 'secured'))
    && (!authType || op.authTypes.includes(authType))
    && (!nested || (nested === 'with' ? op.nestedCalls?.length > 0 : op.nestedCalls?.length === 0))
    && services.every(service => op.awsServices?.includes(service))
    && callers.every(app => op.calledBy?.includes(app))
    && words.every(word => op.path.toLowerCase().includes(word)));
}
const ACCESS_FILTERS = [['', 'All'], ['secured', 'Authorized'], ['public', 'Non-authorized']];
const AUTH_TYPE_FILTERS = [['', 'All'], ['token', 'Token'], ['apiKey', 'API key']];
const NESTED_FILTERS = [['', 'All'], ['with', 'With'], ['without', 'Without']];
const countBy = (operations, field) => operations.reduce((counts, op) => {
  (op[field] || []).forEach(item => { counts[item] = (counts[item] || 0) + 1; });
  return counts;
}, {});
// Services nearly every API's role allows, listed after the ones that tell APIs apart
const COMMON_SERVICES = ['SSM', 'VPC', 'CloudWatch Logs'];
const SERVICES_SHOWN = 5;
const SIDEBAR_KEY = 'ostrum-portal-sidebar';
const PANELS_KEY = 'ostrum-portal-folded-panels';
// The AWS services the APIs' roles allow: the most used first, the near-universal ones last
export function serviceList(operations) {
  const counts = countBy(operations, 'awsServices');
  return Object.keys(counts).sort((a, b) => COMMON_SERVICES.includes(a) - COMMON_SERVICES.includes(b) || counts[b] - counts[a] || a.localeCompare(b));
}
const hostOf = url => { try { return new URL(url).hostname; } catch { return ''; } };
const subdomainOf = url => hostOf(url).split('.')[0];
// A server's environment is its host without the first label: api.test.ostrumtech.com → test.ostrumtech.com.
export const envOf = url => hostOf(url).split('.').slice(1).join('.');
// The environments the servers span, named by their first host label: test.ostrumtech.com → test.
export function environments(servers = []) {
  const found = new Map();
  servers.forEach(({ url }) => {
    const key = envOf(url);
    if (key && !found.has(key)) found.set(key, key.split('.')[0]);
  });
  return [...found].map(([key, name]) => ({ key, name }));
}
// Switching environment keeps the same host where it exists there (pdapi.dev → pdapi.test), else its first server.
export function serverInEnvironment(servers = [], env, currentUrl) {
  const urls = servers.map(server => server.url).filter(url => envOf(url) === env);
  return urls.find(url => subdomainOf(url) === subdomainOf(currentUrl)) || urls[0] || '';
}
// An endpoint's folder is its path's first segment; an AppSync function's is its group (Queries, Mutations).
const folderOf = op => op.appsync ? op.tags[0] : op.path.split('/')[1] || '';
const firstServer = system => system.specSelectors?.servers?.()?.getIn?.([0, 'url']) || '';
// The server for a group or endpoint: the first of its tags with an `x-server-subdomain` (its own,
// else its repository's in x-tagGroups), in the current server's environment
// (api.test.… → pdapi.test.…). '' leaves the server as it is, never another environment.
export function matchingServer(spec, tags, currentUrl) {
  const groups = (spec['x-tagGroups'] || []).filter(group => group?.name);
  const subdomain = tags.map(tag => spec.tags?.find(item => item?.name === tag)?.['x-server-subdomain']
    || groups.find(group => group.tags?.includes(tag))?.['x-server-subdomain']).find(Boolean);
  const environment = envOf(currentUrl);
  if (!subdomain || !environment) return '';
  return (spec.servers || []).map(server => server.url).find(url => hostOf(url) === `${subdomain}.${environment}`) || '';
}
// What Swagger UI is given: the document with only the open endpoint's operation. Every spec it is given, it stringifies,
// re-parses and resolves on the page, and against all ~400 operations that froze the UI for seconds (over 10 s when an
// endpoint opened). $refs into other paths (bundling can point a repeated file at its first use) are replaced by what they
// point to. Without an endpoint the document has no paths, so Swagger UI starts at once for the Overview and server pickers.
export function specForOperation(spec, path, method) {
  const pointed = ref => ref.slice(2).split('/').reduce((value, key) => value?.[key.replace(/~1/g, '/').replace(/~0/g, '~')], spec);
  const inline = (value, seen = []) => {
    if (!value || typeof value !== 'object') return value;
    if (typeof value.$ref === 'string' && value.$ref.startsWith('#/paths/')) {
      return seen.includes(value.$ref) ? {} : inline(pointed(value.$ref), [...seen, value.$ref]);
    }
    if (Array.isArray(value)) return value.map(item => inline(item, seen));
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, inline(item, seen)]));
  };
  const item = path && spec.paths?.[path];
  const operation = item?.[method] && inline({ ...(item.parameters ? { parameters: item.parameters } : {}), [method]: item[method] });
  return { ...spec, paths: operation ? { [path]: operation } : {} };
}
// An operation's x-ostrum: how it is built, written by OT-OSTRUM-API's generate-swagger.js. Bundling can leave it as
// a $ref to the same details elsewhere in the spec; null when the operation has none.
export function implementationOf(spec, path, method) {
  let details = spec.paths?.[path]?.[method]?.['x-ostrum'];
  for (let hops = 0; typeof details?.$ref === 'string' && details.$ref.startsWith('#/') && hops < 5; hops++) {
    details = details.$ref.slice(2).split('/').reduce((value, key) => value?.[key.replace(/~1/g, '/').replace(/~0/g, '~')], spec);
  }
  return details && typeof details === 'object' && !details.$ref ? details : null;
}
const TIME_FORMAT = { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' };
// A change's time in the viewer's local time: 2026-10-24T03:54:16+05:30 is 24 Oct 2026, 03:54 in India and
// 23 Oct 2026, 23:24 in London. timeZone replaces the viewer's (for tests). Anything that isn't a time is shown as it is.
export function changeTime(value, timeZone) {
  const time = /^\d{4}-\d{2}-\d{2}T/.test(value || '') ? new Date(value) : null;
  if (!time || Number.isNaN(time.getTime())) return value || '-';
  return new Intl.DateTimeFormat('en-GB', { ...TIME_FORMAT, timeZone }).format(time);
}
// The documented operation a nested REST call reaches, by method and path on any host; undefined when none.
export const callTarget = (operations, call) => call.type === 'REST'
  ? operations.find(op => op.method === (call.method || 'get').toLowerCase() && op.path === (call.url || '').replace(/^[a-z]+:\/\/[^/]+/i, '').split('?')[0])
  : undefined;

function PortalOperations({ specSelectors, getComponent }) {
  const { view, selected } = useContext(Navigation);
  // The response section renders into its own card after the operation card (see ResponseSlot).
  const [responseSlot, setResponseSlot] = useState(null);
  if (view !== 'reference' || !selected || selected.appsync) return null;
  const Operation = getComponent('OperationContainer', true);
  let match;
  specSelectors.taggedOperations().forEach((group, tag) => {
    if (match) return;
    const op = group.get('operations').find(item => `${item.get('method')}:${item.get('path')}` === selected.id);
    if (op) match = { op, tag };
  });
  return match ? <div className="endpoint-workspace">
    <ResponseSlot.Provider value={responseSlot}>
      <Operation key={selected.id} op={match.op} tag={match.tag} path={selected.path} method={selected.method} specPath={match.op.get('specPath')} />
    </ResponseSlot.Provider>
    <div className="response-card" ref={setResponseSlot} />
  </div> : <LoadingStatus>Loading endpoint…</LoadingStatus>;
}
// Read-only marker on a catalog card; the token itself is supplied with Authorize in the endpoint view.
function AuthLock({ secured }) {
  const label = secured ? 'Requires authorization' : 'No authorization needed';
  return <span className={secured ? 'auth-lock is-locked' : 'auth-lock'} role="img" aria-label={label} title={label}>
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
      <rect x="3" y="7" width="10" height="7" rx="1.5" fill="currentColor" />
      <path d={secured ? 'M5.5 7V5a2.5 2.5 0 0 1 5 0v2' : 'M5.5 7V5a2.5 2.5 0 0 1 4.9-.7'} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  </span>;
}
// A Jira ID links to its issue when the spec's root has `x-jira-browse-url`; "-" (none given) never links.
function JiraId({ id, browseUrl }) {
  return browseUrl && id && id !== '-' ? <a href={`${browseUrl}${encodeURIComponent(id)}`} target="_blank" rel="noreferrer">{id}</a> : <span>{id || '-'}</span>;
}
const HISTORY_SHOWN = 5;
// The apps an API can be called from, by code and name
const CALLER_APPS = [['OW', 'Ostrum Web'], ['PW', 'Purple Web'], ['OA', 'Ostrum App'], ['PD', 'Purple Door']];
const appName = code => CALLER_APPS.find(([item]) => item === code)?.[1] || code;
// What a request sends for each AppSync authorization mode (the tooltip of its chip)
const AUTH_MODE_NOTES = {
  'Cognito user pools': 'Token: a Cognito user pool JWT in the Authorization header',
  OIDC: 'Token: an OpenID Connect JWT in the Authorization header',
  'Lambda authorizer': 'Token: checked by a Lambda authorizer',
  IAM: 'A request signed with AWS credentials (SigV4)',
  'API key': 'API key: the x-api-key header',
};
// How the open endpoint is built (its x-ostrum), under its request and response cards. A nested call to an endpoint
// the spec documents can be opened from here. An AppSync function (graphql) has no update history: it shows its function,
// role, nested calls, callers and authorization modes.
function Implementation({ details, browseUrl, operations, operationId, onOpen, graphql }) {
  const [allHistory, setAllHistory] = useState(false);
  const services = details.awsServices || [];
  // no nestedCalls: the generator couldn't read the handler, so they are unknown rather than none
  const calls = details.nestedCalls;
  const history = details.history || [];
  // Called by: the apps the generator found calling it (no calledBy: they weren't read), and API when documented endpoints'
  // or AppSync functions' nested REST calls reach it (they are named in its tooltip). An AppSync function's can also name a
  // backend (Orchestrator).
  const apps = Array.isArray(details.calledBy) ? details.calledBy : null;
  const apiCallers = graphql ? [] : operations.filter(op => op.id !== operationId && op.nestedCalls?.some(call => callTarget(operations, call)?.id === operationId));
  const noCaller = graphql ? 'No call to it was found in OW, PW, OA or PD, nor in the Orchestrator or Publisher'
    : 'No call to it was found in OW, PW, OA or PD, nor in a documented API';
  return <section className="implementation-card" aria-labelledby="implementation-title">
    <h2 id="implementation-title">Implementation</h2>
    <dl className="implementation-facts">
      <div><dt>Lambda function</dt><dd><code>{details.function || '-'}</code></dd></div>
      <div><dt>IAM role</dt><dd><code>{details.iamRole || '-'}</code></dd></div>
      <div><dt>AWS services</dt><dd>{services.length ? <ul className="service-chips" aria-label="AWS services">{services.map(service => <li key={service}>{service}</li>)}</ul> : '-'}</dd></div>
      <div className={calls?.length ? 'is-wide' : undefined}><dt>Nested calls</dt><dd>{!Array.isArray(calls) ? '-' : calls.length ? <ul className="nested-calls" aria-label="Nested calls">{calls.map((call, index) => {
        const target = callTarget(operations, call);
        return <li key={index}>
          <span className="call-type">{call.type}</span>
          <code>{call.type === 'REST' ? [call.method, call.url].filter(Boolean).join(' ') : call.function}</code>
          {target && <button type="button" onClick={() => onOpen(target)}>Open endpoint</button>}
        </li>;
      })}</ul> : 'None'}</dd></div>
      <div><dt>Called by</dt><dd>{apps?.length || apiCallers.length ? <ul className="service-chips" aria-label="Called by">
        {(apps || []).map(app => <li key={app} title={appName(app)}>{app}</li>)}
        {apiCallers.length > 0 && <li title={apiCallers.map(op => `${op.method.toUpperCase()} ${op.path}`).join('\n')}>API</li>}
      </ul> : apps ? <span title={noCaller}>None found</span> : '-'}</dd></div>
      {graphql && <div><dt>Authorization</dt><dd>{details.authModes?.length ? <ul className="service-chips" aria-label="Authorization modes">
        {details.authModes.map(mode => <li key={mode} title={AUTH_MODE_NOTES[mode]}>{mode}</li>)}
      </ul> : '-'}</dd></div>}
      {!graphql && <div><dt>Last updated</dt><dd>{details.lastUpdated ? <><JiraId id={details.lastUpdated.jira} browseUrl={browseUrl} /> · {changeTime(details.lastUpdated.date)}</> : '-'}</dd></div>}
    </dl>
    {history.length > 0 && <div className="implementation-history">
      <h3>Change history</h3>
      <table>
        <thead><tr><th scope="col">Jira ID</th><th scope="col">Updated</th></tr></thead>
        <tbody>{(allHistory ? history : history.slice(0, HISTORY_SHOWN)).map((entry, index) => <tr key={index}>
          <td><JiraId id={entry.jira} browseUrl={browseUrl} /></td><td>{changeTime(entry.date)}</td>
        </tr>)}</tbody>
      </table>
      {history.length > HISTORY_SHOWN && <button type="button" aria-expanded={allHistory} onClick={() => setAllHistory(!allHistory)}>{allHistory ? 'Show fewer' : `Show all ${history.length}`}</button>}
    </div>}
  </section>;
}
// Read-only marker on a catalog card whose API calls other APIs or Lambdas; its tooltip names the kinds of call.
function NestedCallsIcon({ calls }) {
  const kinds = [...new Set(calls.map(call => call.type).filter(Boolean))].join(', ');
  return <span className="nested-icon" role="img" aria-label="Makes nested calls" title={`Nested calls: ${kinds}`}>
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
      <g fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="4" cy="8" r="2" /><circle cx="12" cy="3.5" r="2" /><circle cx="12" cy="12.5" r="2" />
        <path d="M5.8 7.1l4.4-2.5M5.8 8.9l4.4 2.5" strokeLinecap="round" />
      </g>
    </svg>
  </span>;
}
// A sidebar panel whose heading folds it to the heading alone; whatever its filters chose stays applied while folded.
// Its content can name itself after the heading with aria-labelledby={`${id}-label`}.
function FoldingSection({ id, label, folded, onToggle, className, children }) {
  return <section className={`nav-section${className ? ` ${className}` : ''}`} aria-labelledby={`${id}-label`}>
    <button type="button" id={`${id}-label`} className="nav-label nav-panel-toggle" aria-expanded={!folded} aria-controls={id} onClick={onToggle}>
      {label}<span className="nav-panel-chevron" aria-hidden="true">›</span>
    </button>
    <div id={id} hidden={folded}>{children}</div>
  </section>;
}
function LoadingStatus({ children }) {
  return <div className="portal-loading" role="status"><span className="portal-spinner" aria-hidden="true" />{children}</div>;
}
// Swagger's content-type picker (request body, response media type) shows only when there is a real choice.
// A single type stays mounted but hidden: on mount it selects that type, which Swagger needs to build requests.
export const contentTypeWrapper = Original => function SingleContentType(props) {
  return props.contentTypes?.size > 1 ? <Original {...props} /> : <div className="single-content-type" hidden><Original {...props} /></div>;
};
const PortalPlugin = () => ({
  components: { operations: PortalOperations },
  wrapComponents: {
    ...responseWrappers,
    contentType: contentTypeWrapper,
    SyntaxHighlighter: Original => function LightSyntaxHighlighter(props) {
      return <Original {...props} getConfigs={() => ({
        ...props.getConfigs(),
        syntaxHighlight: { ...props.getConfigs().syntaxHighlight, theme: 'idea' },
      })} />;
    },
    InfoContainer: Original => function PortalInfo(props) {
      const { view } = useContext(Navigation);
      return view === 'overview' ? <Original {...props} /> : null;
    },
  },
});
const plugins = [PortalPlugin];
// Swagger UI and its libraries are most of the portal's script, and only an open endpoint needs them, so they come in a
// script of their own, fetched once the spec has loaded while the catalog is already showing.
const SwaggerUI = lazy(() => import('swagger-ui-react'));
// The upstream wrapper can re-submit its object spec on a parent render.
// Keep its instance stable; context updates only the navigation-aware plugins.
const SwaggerWorkspace = memo(function SwaggerWorkspace({ spec, onComplete }) {
  return <SwaggerUI spec={spec} plugins={plugins} docExpansion="full" defaultModelsExpandDepth={-1}
    tryItOutEnabled={true} supportedSubmitMethods={['get', 'post', 'put', 'delete']}
    displayRequestDuration={true} showExtensions={false} showCommonExtensions={false} onComplete={onComplete} />;
});

export default function ApiPortal({ account, onLogout }) {
  const [spec, setSpec] = useState(null);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [view, setView] = useState('reference');
  const [query, setQuery] = useState('');
  const [tag, setTag] = useState('');
  const [access, setAccess] = useState('');
  const [authType, setAuthType] = useState('');
  const [nested, setNested] = useState('');
  const [services, setServices] = useState([]);
  const [callers, setCallers] = useState([]);
  const [allServices, setAllServices] = useState(false);
  const [selected, setSelected] = useState(null);
  const [server, setServer] = useState('');
  const [collapsed, setCollapsed] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  // On wider screens the sidebar can be hidden; this browser remembers the choice (when it allows storage).
  const [sidebarOpen, setSidebarOpen] = useState(() => { try { return localStorage.getItem(SIDEBAR_KEY) !== 'hidden'; } catch { return true; } });
  useEffect(() => { try { localStorage.setItem(SIDEBAR_KEY, sidebarOpen ? 'shown' : 'hidden'); } catch { /* not remembered */ } }, [sidebarOpen]);
  // The sidebar panels folded by their heading (called-from, aws-services), remembered the same way
  const [foldedPanels, setFoldedPanels] = useState(() => {
    try { const stored = JSON.parse(localStorage.getItem(PANELS_KEY)); return Array.isArray(stored) ? stored : []; } catch { return []; }
  });
  useEffect(() => { try { localStorage.setItem(PANELS_KEY, JSON.stringify(foldedPanels)); } catch { /* not remembered */ } }, [foldedPanels]);
  const togglePanel = name => setFoldedPanels(current => current.includes(name) ? current.filter(item => item !== name) : [...current, name]);
  const [splitView, setSplitView] = useState(false);
  const [page, setPage] = useState(0);
  const pageSize = 40;
  useEffect(() => {
    const controller = new AbortController();
    setError('');
    fetch(specUrl, { signal: controller.signal }).then(response => {
      if (!response.ok) throw new Error(`Unable to load API documentation (${response.status}).`);
      return response.text();
    }).then(text => parseSpecification(text, controller.signal)).then(document => {
      if (!controller.signal.aborted) setSpec(document);
    }).catch(err => { if (err.name !== 'AbortError') setError(err.message); });
    return () => controller.abort();
  }, [attempt]);
  const operations = useMemo(() => spec ? listOperations(spec) : [], [spec]);
  const tags = useMemo(() => [...new Set(operations.flatMap(op => op.tags))], [operations]);
  const groups = useMemo(() => spec ? repositoryGroups(spec, tags) : [], [spec, tags]);
  const deferredQuery = useDeferredValue(query);
  const results = useMemo(() => searchOperations(operations, deferredQuery, tag, access, authType, nested, services, callers),
    [operations, deferredQuery, tag, access, authType, nested, services, callers]);
  useEffect(() => { setPage(0); }, [deferredQuery, tag, access, authType, nested, services, callers]);
  // Non-authorized APIs have no auth type, so choosing them clears and disables that filter.
  const chooseAccess = value => { setAccess(value); if (value === 'public') setAuthType(''); };
  const currentPage = Math.min(page, Math.max(0, Math.ceil(results.length / pageSize) - 1));
  const visibleResults = results.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
  // Sidebar counts follow the Authorization, Auth type, Nested calls, AWS services and Called from filters, not the path search
  // or the open group.
  const counted = useMemo(() => searchOperations(operations, '', '', access, authType, nested, services, callers),
    [operations, access, authType, nested, services, callers]);
  const tagCounts = useMemo(() => countBy(counted, 'tags'), [counted]);
  const repoCounts = useMemo(() => countBy(counted, 'repos'), [counted]);
  // Apps are chosen like services: the APIs called from all the chosen ones are listed, and an app's count is of the APIs
  // matching the filters, chosen apps included, so an unchosen app's count is what choosing it as well would leave.
  const appCounts = useMemo(() => countBy(counted, 'calledBy'), [counted]);
  const toggleCaller = code => {
    setCallers(current => current.includes(code) ? current.filter(item => item !== code) : [...current, code]);
    setSelected(null); setView('reference');
  };
  // Every service stays listed in one order; its count is of the APIs matching the filters, selected services included,
  // so an unselected service's count is what choosing it as well would leave.
  const serviceNames = useMemo(() => serviceList(operations), [operations]);
  const serviceCounts = useMemo(() => countBy(counted, 'awsServices'), [counted]);
  // Folded, the list shows the first five and any selected service beyond them.
  const shownServices = allServices ? serviceNames : serviceNames.filter((name, index) => index < SERVICES_SHOWN || services.includes(name));
  const toggleService = name => {
    setServices(current => current.includes(name) ? current.filter(item => item !== name) : [...current, name]);
    setSelected(null); setView('reference');
  };
  const navigation = useMemo(() => ({ view, selected }), [view, selected]);
  // Swagger UI keeps the last opened endpoint's slice when you go back to the catalog, so returning to it is instant and
  // nothing is re-processed; it stays mounted throughout, keeping Authorize tokens and the selected server.
  const [swaggerTarget, setSwaggerTarget] = useState(null);
  const swaggerSpec = useMemo(() => spec && specForOperation(spec, swaggerTarget?.path, swaggerTarget?.method), [spec, swaggerTarget]);
  const swagger = useRef(null);
  // The portal's Server picker drives Swagger's selected server; Swagger's own picker stays hidden.
  const rememberSwagger = useCallback(system => {
    swagger.current = system;
    const current = system.oas3Selectors.selectedServer() || firstServer(system);
    if (current && !system.oas3Selectors.selectedServer()) system.oas3Actions.setSelectedServer(current);
    setServer(current);
  }, []);
  const applyServer = url => { swagger.current?.oas3Actions.setSelectedServer(url); setServer(url); };
  // The header's Environment is the selected server's; changing it moves the server to that environment.
  const environmentList = useMemo(() => environments(spec?.servers), [spec]);
  const environment = envOf(server);
  const chooseEnvironment = env => {
    const url = serverInEnvironment(spec?.servers, env, server);
    if (url) applyServer(url);
  };
  // Preselect only when something is picked, so a server chosen by hand afterwards stays.
  const selectServer = tagsOfSelection => {
    const system = swagger.current;
    const url = system && matchingServer(spec, tagsOfSelection, system.oas3Selectors.selectedServer());
    if (url) applyServer(url);
  };
  const name = account?.name || 'User';
  const initials = name.split(/\s+/).map(part => part[0]).slice(0, 2).join('');
  const openCatalog = nextTag => {
    setTag(nextTag); setSelected(null); setView('reference'); setMenuOpen(false); window.scrollTo({ top: 0 });
    if (nextTag) selectServer([nextTag]);
  };
  const toggleRepo = name => setCollapsed(current => current.includes(name) ? current.filter(item => item !== name) : [...current, name]);
  // An AppSync function has no Swagger operation: Swagger UI keeps the last endpoint's slice and the server stays.
  const choose = op => {
    setSelected(op); setView('reference'); window.scrollTo({ top: 0 });
    if (!op.appsync) { setSwaggerTarget(op); selectServer(op.tags); }
  };
  // A repository row is selected when it holds the open group.
  const activeRepo = view === 'reference' ? groups.find(group => group.name && group.tags.includes(tag))?.name : '';
  // The All endpoints list has no heading, to leave room for the list. A group's title is its repository
  // and folder (first path segment); an endpoint shows that line above its path.
  const showHeading = view === 'overview' || !!selected || !!tag;
  const crumbOp = view === 'reference' && (selected || (tag && operations.find(op => op.tags.includes(tag))));
  const eyebrow = view === 'overview' ? 'OSTRUM / GET STARTED'
    : crumbOp ? [crumbOp.repos[0], folderOf(crumbOp)].filter(Boolean).join(' / ') : '';
  const endpointOpen = view === 'reference' && !!selected;
  // An open AppSync function shows only its Implementation card: no Swagger UI, Authorize or workspace layout.
  const swaggerOpen = endpointOpen && !selected.appsync;
  const implementation = endpointOpen && spec ? selected.appsync || implementationOf(spec, selected.path, selected.method) : null;
  const sidebarLabel = sidebarOpen ? 'Hide navigation' : 'Show navigation';
  return <div className={`portal dashboard ${splitView ? 'split-view' : 'stacked-view'}${swaggerOpen ? ' endpoint-open' : ''}${sidebarOpen ? '' : ' sidebar-collapsed'}`}>
    <a href="#api-main" className="skip-link">Skip to documentation</a>
    <header className="portal-header">
      <div className="portal-brand">
        <button type="button" className="sidebar-toggle" aria-controls="api-navigation" aria-expanded={sidebarOpen} aria-label={sidebarLabel} title={sidebarLabel} onClick={() => setSidebarOpen(!sidebarOpen)}>
          <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
            <rect x="1.5" y="2.5" width="13" height="11" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
            <path d={sidebarOpen ? 'M2.2 3.2h3.6v9.6H2.2z' : 'M6 3v10'} fill="currentColor" stroke="currentColor" strokeWidth={sidebarOpen ? 0 : 1.3} />
          </svg>
        </button>
        <img src="/logo-white.png" alt="Ostrum" /><span>Developer portal</span>
      </div>
      <div className="header-pickers">
        <label className="portal-header-label env-select">
          <span className="header-picker-label">Environment</span>
          <select value={environment} disabled={!environment} onChange={event => chooseEnvironment(event.target.value)}>
            {environmentList.map(item => <option key={item.key} value={item.key}>{item.name}</option>)}
          </select>
        </label>
        {/* Only the chosen environment's servers, by URL: the environment is already named beside it. */}
        <label className="portal-header-label header-server"><span className="header-picker-label">Server</span>
          <select value={server} disabled={!server} onChange={event => applyServer(event.target.value)}>
            {(spec?.servers || []).filter(item => !environment || envOf(item.url) === environment).map(item => <option key={item.url} value={item.url}>{item.url}</option>)}
          </select>
        </label>
      </div>
      <details className="account-menu"><summary aria-label="Account menu"><span className="avatar">{initials}</span><span>{name}</span></summary>
        <div className="account-popover"><strong>{name}</strong><p>{account?.username}</p><small>Microsoft portal sign-in</small><button onClick={onLogout}>Sign out</button></div>
      </details>
    </header>
    <button className="navigation-toggle" aria-expanded={menuOpen} aria-controls="api-navigation" onClick={() => setMenuOpen(!menuOpen)}>Browse documentation</button>
    <div className="portal-layout">
      <aside id="api-navigation" className={`portal-sidebar ${menuOpen ? 'is-visible' : ''}`}>
        <p className="nav-label">WORKSPACE</p>
        <button className={view === 'overview' ? 'nav-item active' : 'nav-item'} onClick={() => { setView('overview'); setMenuOpen(false); }}>Overview</button>
        <button className={view === 'reference' && !tag ? 'nav-item active' : 'nav-item'} onClick={() => openCatalog('')}>All endpoints <span>{counted.length}</span></button>
        <section className="nav-section" aria-labelledby="api-groups-label">
          <p className="nav-label" id="api-groups-label">API GROUPS</p>
          <nav aria-label="API groups">{groups.map((group, index) => {
            const open = !collapsed.includes(group.name);
            return <div key={group.name} className="nav-group" role="group" aria-label={group.name || 'All groups'}>
              {group.name && <button className={group.name === activeRepo ? 'nav-repo active' : 'nav-repo'} aria-expanded={open} aria-controls={`nav-tags-${index}`} onClick={() => toggleRepo(group.name)}>
                <span className="nav-repo-chevron" aria-hidden="true">›</span><span className="nav-repo-name">{group.name}</span><span className="nav-repo-count">{repoCounts[group.name] || 0}</span>
              </button>}
              <div id={`nav-tags-${index}`} className={group.name ? 'nav-repo-tags' : undefined} hidden={!open}>{group.tags.map(item => <button key={item} className={`nav-item${view === 'reference' && tag === item ? ' active' : ''}${tagCounts[item] ? '' : ' is-empty'}`} onClick={() => openCatalog(item)}>
                {readableTag(item)}<span>{tagCounts[item] || 0}</span>
              </button>)}</div>
            </div>;
          })}</nav>
        </section>
        {/* Checkboxes like AWS SERVICES' */}
        <FoldingSection id="called-from" label="CALLED FROM" className="service-filter" folded={foldedPanels.includes('called-from')} onToggle={() => togglePanel('called-from')}>
          {callers.length > 1 && <p className="service-filter-hint">APIs called from all {callers.length}</p>}
          <div role="group" aria-labelledby="called-from-label">{CALLER_APPS.map(([code, name]) =>
            <label key={code} className={`service-option${appCounts[code] ? '' : ' is-empty'}`}>
              <input type="checkbox" checked={callers.includes(code)} onChange={() => toggleCaller(code)} />
              <span className="service-option-name"><span className="service-option-code">{code}</span> {name}</span>{' '}<span className="service-option-count">{appCounts[code] || 0}</span>
            </label>)}</div>
          {callers.length > 0 && <div className="service-filter-actions"><button type="button" onClick={() => setCallers([])}>Clear</button></div>}
        </FoldingSection>
        {serviceNames.length > 0 && <FoldingSection id="aws-services" label="AWS SERVICES" className="service-filter" folded={foldedPanels.includes('aws-services')} onToggle={() => togglePanel('aws-services')}>
          {services.length > 1 && <p className="service-filter-hint">APIs whose role allows all {services.length}</p>}
          <div role="group" aria-labelledby="aws-services-label">{shownServices.map(name =>
            <label key={name} className={`service-option${serviceCounts[name] ? '' : ' is-empty'}`}>
              <input type="checkbox" checked={services.includes(name)} onChange={() => toggleService(name)} />
              <span className="service-option-name">{name}</span>{' '}<span className="service-option-count">{serviceCounts[name] || 0}</span>
            </label>)}</div>
          <div className="service-filter-actions">
            {(allServices || shownServices.length < serviceNames.length) && <button type="button" aria-expanded={allServices} onClick={() => setAllServices(!allServices)}>
              {allServices ? 'Show fewer' : `Show ${serviceNames.length - shownServices.length} more`}
            </button>}
            {services.length > 0 && <button type="button" onClick={() => setServices([])}>Clear</button>}
          </div>
        </FoldingSection>}
      </aside>
      <main id="api-main" className={`portal-main ${view === 'overview' ? 'overview-mode' : ''}`} tabIndex={-1}>
        {showHeading && <div className="page-heading"><div>
          {endpointOpen && <button className="back-button" onClick={() => setSelected(null)}>← Back to endpoints</button>}
          {view === 'reference' && !selected ? <h1>{eyebrow}</h1> : <>
            <p className="eyebrow">{eyebrow}</p>
            <h1>{view === 'overview' ? 'Platform overview' : selected.path}</h1>
            {view === 'overview' ? <p>Platform capabilities and integration guidance.</p>
              : selected.summary && <p className="heading-summary">{selected.summary}</p>}
          </>}
        </div>
          {swaggerOpen && <div className="workspace-layout" role="group" aria-label="Workspace layout">
            <button aria-pressed={!splitView} onClick={() => setSplitView(false)}>Stacked</button>
            <button aria-pressed={splitView} onClick={() => setSplitView(true)}>Split view</button>
          </div>}
        </div>}
        {error ? <div className="portal-state" role="alert"><h2>Documentation could not load</h2><p>{error}</p><button onClick={() => setAttempt(attempt + 1)}>Try again</button></div> : !spec ? <LoadingStatus>Loading API documentation…</LoadingStatus> : <>
          {view === 'reference' && !selected && <div className="reference-tools">
            <label className="endpoint-search"><span>Search endpoints</span><input type="search" placeholder="Search by endpoint path…" value={query} onChange={event => setQuery(event.target.value)} /></label>
            <div className="access-filter"><span id="access-filter-label">Authorization</span>
              <div className="segmented" role="group" aria-labelledby="access-filter-label">{ACCESS_FILTERS.map(([value, label]) =>
                <button key={label} type="button" aria-pressed={access === value} onClick={() => chooseAccess(value)}>{label}</button>)}</div>
            </div>
            <div className="access-filter" title={access === 'public' ? 'Non-authorized APIs have no auth type' : undefined}><span id="auth-type-label">Auth type</span>
              <div className="segmented" role="group" aria-labelledby="auth-type-label">{AUTH_TYPE_FILTERS.map(([value, label]) =>
                <button key={label} type="button" disabled={access === 'public'} aria-pressed={authType === value} onClick={() => setAuthType(value)}>{label}</button>)}</div>
            </div>
            <div className="access-filter"><span id="nested-filter-label">Nested calls</span>
              <div className="segmented" role="group" aria-labelledby="nested-filter-label">{NESTED_FILTERS.map(([value, label]) =>
                <button key={label} type="button" aria-pressed={nested === value} onClick={() => setNested(value)}>{label}</button>)}</div>
            </div>
          </div>}
          <Navigation.Provider value={navigation}>
            <Suspense fallback={swaggerOpen ? <LoadingStatus>Loading endpoint…</LoadingStatus> : null}>
              <SwaggerWorkspace spec={swaggerSpec} onComplete={rememberSwagger} />
            </Suspense>
          </Navigation.Provider>
          {implementation && <Implementation key={selected.id} details={implementation} browseUrl={spec['x-jira-browse-url']} operations={operations} operationId={selected.id} onOpen={choose} graphql={!!selected.appsync} />}
          {view === 'reference' && !selected && <section className="endpoint-catalog" aria-label="Endpoints">
            {results.length ? visibleResults.map(op => <button key={op.id} className="endpoint-card" onClick={() => choose(op)}>
              <span className={`method-badge method-${op.method}`}>{op.method.toUpperCase()}</span>
              <span className="endpoint-copy"><code>{op.path}</code>{op.summary && <span title={op.summary.length > SUMMARY_LIMIT ? op.summary : undefined}>{shortSummary(op.summary)}</span>}
                {op.deprecated && <span className="deprecated-label">Deprecated</span>}</span>
              {op.repos.length > 0 && <span className="repo-label">{op.repos.join(', ')}</span>}
              {op.nestedCalls?.length > 0 && <NestedCallsIcon calls={op.nestedCalls} />}{op.secured !== null && <AuthLock secured={op.secured} />}<span className="endpoint-arrow" aria-hidden="true">→</span>
            </button>) : <div className="portal-state"><h2>No matching endpoints</h2><p>Try another path or clear the search, group, and filters.</p><button onClick={() => { setQuery(''); setTag(''); setAccess(''); setAuthType(''); setNested(''); setServices([]); setCallers([]); }}>Clear filters</button></div>}
          </section>}
          {view === 'reference' && !selected && results.length > pageSize && <nav className="catalog-pagination" aria-label="Endpoint pages">
            <span>{currentPage * pageSize + 1}–{Math.min((currentPage + 1) * pageSize, results.length)} of {results.length}</span>
            <button disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)}>Previous</button>
            <button disabled={(currentPage + 1) * pageSize >= results.length} onClick={() => setPage(currentPage + 1)}>Next</button>
          </nav>}
        </>}
      </main>
    </div>
  </div>;
}
